import styled, { css } from 'styled-components';

/* ===== Identidade visual (alinhada ao login / Header) =====
   --primary: #ff6d3c   --blue: #1789dd
   --ink:     #17263b / #2c405a
   --muted:   #748196 / #8fa0ba
   --border:  #dbe1e9 / rgba(216, 224, 235, 0.9)
   As cores semânticas dos badges (tipo/situação) são preservadas,
   apenas tornadas mais legíveis e modernas.
=========================================================== */

/* tons por TIPO (mantém o significado das cores originais) */
const typeTones = {
  1: {
    fg: '#2f7d52',
    bg: 'rgba(83, 128, 97, 0.12)',
    bd: 'rgba(83, 128, 97, 0.5)',
  },
  2: {
    fg: '#a87a12',
    bg: 'rgba(214, 167, 23, 0.14)',
    bd: 'rgba(214, 167, 23, 0.5)',
  },
  3: {
    fg: '#1f6fd6',
    bg: 'rgba(84, 133, 247, 0.12)',
    bd: 'rgba(84, 133, 247, 0.5)',
  },
  4: {
    fg: '#c0291c',
    bg: 'rgba(176, 24, 16, 0.1)',
    bd: 'rgba(176, 24, 16, 0.45)',
  },
  default: {
    fg: '#64748b',
    bg: 'rgba(148, 163, 184, 0.12)',
    bd: 'rgba(148, 163, 184, 0.5)',
  },
};

/* tons por SITUAÇÃO/STATUS */
const statusTones = {
  1: {
    fg: '#a87a12',
    bg: 'rgba(214, 167, 23, 0.14)',
    bd: 'rgba(214, 167, 23, 0.5)',
  },
  2: {
    fg: '#2f7d52',
    bg: 'rgba(83, 128, 97, 0.12)',
    bd: 'rgba(83, 128, 97, 0.5)',
  },
  default: {
    fg: '#64748b',
    bg: 'rgba(148, 163, 184, 0.12)',
    bd: 'rgba(148, 163, 184, 0.5)',
  },
};

const handleColorType = (id) => typeTones[id] || typeTones.default;
const handleColorStatus = (id) => statusTones[id] || statusTones.default;

/* estilo base compartilhado pelos badges em pílula */
const badgeBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 6px 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

export const Container = styled.div`
  padding-bottom: 24px;

  > h2 {
    padding: 22px 10px 10px;
    color: #17263b;
    font-size: 22px;
    letter-spacing: -0.4px;
  }

  > strong {
    display: block;
    padding: 0 10px 10px;
    color: #2c405a;
    font-size: 14px;
  }
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 30px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid rgba(216, 224, 235, 0.9);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(23, 38, 59, 0.06),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
`;

export const TableCatalog = styled.div`
  display: flex;
  height: 500px;
  overflow: auto;
`;

TableCatalog.Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #2c405a;
  font-weight: 600;

  > svg {
    margin: 0 2px;
    color: #1789dd;
  }
`;

TableCatalog.ColumnType = styled.div`
  ${badgeBase}
  width: 100px;
  min-width: 100px;

  ${({ type }) => {
    const tone = handleColorType(type);
    return css`
      color: ${tone.fg};
      background: ${tone.bg};
      border: 1px solid ${tone.bd};
    `;
  }}
`;

TableCatalog.ColumnStatus = styled.div`
  ${badgeBase}
  width: 100px;
  min-width: 100px;

  ${({ status }) => {
    const tone = handleColorStatus(status);
    return css`
      color: ${tone.fg};
      background: ${tone.bg};
      border: 1px solid ${tone.bd};
    `;
  }}
`;

TableCatalog.Menu = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;

  & > button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 10px;
    background: none;
    color: #8fa0ba;
    cursor: pointer;
    transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;

    &:hover {
      transform: translateY(-1px);
    }
  }
`;

TableCatalog.Menu.BtnEdit = styled.button`
  &:hover {
    color: #538061;
    background: rgba(83, 128, 97, 0.12);
  }
`;

TableCatalog.Menu.BtnExtract = styled.button`
  &:hover {
    color: #a87a12;
    background: rgba(214, 167, 23, 0.14);
  }
`;

TableCatalog.Menu.BtnDelete = styled.button`
  &:hover {
    color: #de3b3b;
    background: rgba(222, 59, 59, 0.12);
  }
`;

export const CatalogStatus = styled.span`
  ${badgeBase}
  width: 150px;
  min-width: 150px;

  ${({ type }) => {
    const tone = handleColorStatus(type);
    return css`
      color: ${tone.fg};
      background: ${tone.bg};
      border: 1px solid ${tone.bd};
    `;
  }}

  & + span {
    margin-left: 6px;
  }
`;

export const MessageStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 24px;
  padding: 28px 24px;
  min-height: 100px;

  border: 1px solid rgba(216, 224, 235, 0.9);
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  box-shadow: 0 10px 30px rgba(23, 38, 59, 0.06);
  color: #2c405a;
  text-align: center;
`;

MessageStatus.Title = styled.span`
  font-size: 14px;
  line-height: 1.55;
  color: #2c405a;
`;

MessageStatus.Error = styled.span`
  padding: 5px;
  color: #dd0312;
  font-size: 12px;
  line-height: 1.5;
`;

MessageStatus.BtnAct = styled.button`
  margin-top: 8px;
  padding: 10px 20px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;

  background: linear-gradient(135deg, #ff6d3c 0%, #ff875f 52%, #ff9b78 100%);
  box-shadow: 0 12px 26px rgba(255, 109, 60, 0.32);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 32px rgba(255, 109, 60, 0.4);
    filter: brightness(1.02);
  }

  &:active {
    transform: translateY(0);
  }
`;

MessageStatus.BtnError = styled.button`
  margin-top: 8px;
  padding: 10px 20px;
  border: 2px solid #dd0312;
  border-radius: 12px;
  background: none;
  color: #dd0312;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: #dd0312;
    color: #fff;
    box-shadow: 0 12px 26px rgba(221, 3, 18, 0.28);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;
